/**
 * Customized to fix espot link URLs that contain query strings or other special characters.
 *
 * The core implementation passes the full href string (e.g. `/s/ryobI?categoria=aspiradoras`)
 * directly as the `pathname` of a Next.js UrlObject. Next.js then URL-encodes special characters
 * like `?` in the pathname, producing broken URLs like `/s/ryobI%3Fcategoria%3Daspiradoras`.
 *
 * This override keeps all string URLs as strings so that special characters are never
 * re-encoded by Next.js. The store prefix and params-of-concern are appended via plain
 * string concatenation. Absolute URLs (http/https) naturally skip the store prefix, and
 * params-of-concern are only appended when present.
 */

import {
	QueryParameterNameToHandleType,
	QueryParametersOfConcern,
} from '@/data/types/QueryParametersOfConcern';
import { Token } from '@/data/types/Token';
import { extractParamsOfConcern } from '@/data/utils/extractParamsOfConcern';
import { UrlObject } from 'url';

const EMPTY_TOKEN: Token = {};

const buildQueryString = (queryInConcern: QueryParametersOfConcern) =>
	Object.keys(queryInConcern)
		.map((key) => key + '=' + queryInConcern[key as QueryParameterNameToHandleType])
		.join('&');

// eslint-disable-next-line complexity
export const constructNextUrl = (
	asPath: string,
	url?: UrlObject | string,
	storeToken: Token = EMPTY_TOKEN
) => {
	const { urlKeywordName } = storeToken;
	const storePath = urlKeywordName ? `/${urlKeywordName}` : '';
	if (url !== undefined) {
		const { queryOfConcern: queryInConcern } = extractParamsOfConcern(asPath);
		if (typeof url === 'string') {
			// Keep the URL as a string to avoid Next.js re-encoding any special characters.
			// Apply the store prefix only for relative URLs (those starting with `/`).
			const withStorePath =
				storePath && !url.startsWith('http')
					? `${storePath}/${url}`
							.replace('//', '/')
							.replace(/\/$/, '') // trailing slash
							.replace(`${storePath}${storePath}`, storePath)
					: url;
			// Append params-of-concern (e.g. WCPreviewToken, shopAsUser) if present.
			const concernQueryString = buildQueryString(queryInConcern);
			if (!concernQueryString) {
				return withStorePath;
			}
			// Use `&` if the URL already has a `?`, else `?`
			return withStorePath.includes('?')
				? `${withStorePath}&${concernQueryString}`
				: `${withStorePath}?${concernQueryString}`;
		} else {
			return typeof url.pathname === 'string' && !url.pathname.startsWith('http')
				? {
						...url,
						query:
							typeof url.query === 'string'
								? url.query + buildQueryString(queryInConcern)
								: {
										...url.query,
										...queryInConcern,
								  },
						pathname: storePath
							? `${storePath}/${url.pathname}`
									.replace('//', '/')
									.replace(/\/$/, '') // trailing slash
									.replace(`${storePath}${storePath}`, storePath)
							: url.pathname,
				  }
				: url;
		}
	}
	return url;
};
